/**
 * SESIUNEA SE REINNOIESTE CAT TIMP LUCREAZA OMUL, NU CAT TIMP E DESCHISA FILA.
 *
 * -----------------------------------------------------------------------------
 * CE ERA STRICAT
 * -----------------------------------------------------------------------------
 *
 * Cookie-ul `od_v4` tine 30 de minute. In portalul VECHI se rescria la fiecare
 * pagina intreaga (`V4Token::issue`, chemat din `My_Controller`), deci cat timp
 * omul dadea clicuri prin portal nu se apropia niciodata de expirare.
 *
 * `/app` isi incarca pagina O SINGURA DATA si traieste mai departe in ea. Deci
 * nimic nu mai muta ceasul, si singurul lucru care il misca era un 401 deja
 * primit (`client.ts`). Doar ca reinnoirea are nevoie de un jeton VIU: dupa 30
 * de minute si un ceas, cookie-ul e mort, `/api/v2/auth/refresh` raspunde 401,
 * si omul ajunge la ecranul de intrare in mijlocul lucrului. Adica reincercarea
 * de la 401 nu putea salva niciodata tocmai cazul pentru care fusese scrisa.
 *
 * -----------------------------------------------------------------------------
 * CE INSEAMNA „LUCREAZA"
 * -----------------------------------------------------------------------------
 *
 * Omul atinge tastatura, mouse-ul sau ecranul in fila ASTA. Atat.
 *
 * NU cererile catre API: sunt ecrane care se reimprospateaza singure, iar dupa
 * ele o fila uitata pe un birou gol s-ar reinnoi la nesfarsit si cele 30 de
 * minute n-ar mai insemna nimic. NU trecerea timpului: un ceas care bate
 * singur ar tine sesiunea vie exact in cazul in care trebuie sa moara.
 *
 * Deci: se cere un jeton nou cel mult o data la cinci minute, si numai daca
 * omul a atins ceva in ultimele cinci minute. O fila lasata in pace se stinge
 * la cel mult 35 de minute de la ultima atingere - masura ramane cea de
 * dinainte, cu marja unui singur pas.
 *
 * Reinnoirea de la PORNIRE e altceva si trebuie sa existe: cookie-ul e
 * `HttpOnly`, deci pagina nu poate citi cat i-a mai ramas. Cine reincarca la
 * minutul 29 ar astepta altfel cinci minute pentru un jeton care moare peste
 * unul. Incarcarea paginii e oricum fapta unui om, si e chiar ce facea portalul
 * vechi la fiecare pagina.
 *
 * Cat poate tine rostogolirea NU se hotaraste aici: `auth_time` din jeton si
 * `App\Auth\SessionCookie::MAX_SESSION_SECONDS` opresc reinnoirea dupa 12 ore
 * de la parola si cod, si le verifica serverul. O masura pe care o poate ocoli
 * cel masurat nu e o masura.
 */

import { announceIdle, idleMinutesOf } from './idle'

/**
 * Capatul din Symfony care rescrie cookie-ul `od_v4` din sesiunea vie.
 *
 * Era `/v4/session`, din portalul vechi. La adresa noua portalul acela nu
 * exista, deci reinnoirea vine tot din backend-ul nou, pe langa care trece si
 * restul cererilor.
 */
export const SESSION_REFRESH_URL = '/api/v2/auth/refresh'

/** Nu se cere un jeton nou mai des de atat. */
export const RENEW_AFTER_MS = 5 * 60 * 1000

/** ...si numai daca omul a atins ceva in ultimele atatea. */
export const ACTIVE_WITHIN_MS = 5 * 60 * 1000

/** Cat de des se pune intrebarea. Ieftina: de obicei raspunsul e „nu". */
export const CHECK_EVERY_MS = 60 * 1000

/**
 * Ce numaram drept om. `pointerdown` prinde si mouse-ul si degetul; `wheel` e
 * pentru cine citeste un raport lung fara sa apese nimic.
 */
const INPUT_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const

let lastInputAt = 0
let lastRenewAt = 0
let inFlight: Promise<boolean> | null = null

/** Omul a facut ceva. */
export function noteActivity(at: number = Date.now()): void {
  if (at > lastInputAt) lastInputAt = at
}

/** Numai pentru probe: ceasul sesiunii, de la capat. */
export function resetSessionClock(input = 0, renew = 0): void {
  lastInputAt = input
  lastRenewAt = renew
  inFlight = null
}

async function askForNewToken(): Promise<boolean> {
  try {
    /* POST, nu GET: capul din Symfony schimba ceva pe server - rescrie
       cookie-ul - deci raspunde numai la POST. */
    const response = await fetch(SESSION_REFRESH_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })

    if (response.ok) lastRenewAt = Date.now()

    /* Jetonul a expirat de nelucru, dar omul e inca al nostru: bara din josul
       paginii, cu codul pe mail - nu ecranul de intrare. Vezi `api/idle.ts`. */
    if (response.status === 401) {
      const minutes = await idleMinutesOf(response)
      if (minutes !== null) announceIdle(minutes)
    }

    return response.ok
  } catch {
    /* Reteaua cazuta nu e o sesiune expirata, dar nici nu se poate reincerca.
       Ceasul ramane nemutat, deci pasul urmator incearca din nou. */
    return false
  }
}

/**
 * Un jeton nou, si UNA SINGURA cerere pentru el.
 *
 * Cand se deschide un ecran, TanStack Query porneste zece intrebari deodata.
 * Daca jetonul tocmai a murit, toate zece iau 401 in aceeasi clipa - si fara
 * randurile astea ar cere zece jetoane noi, care se rescriu unul peste altul
 * si se bat cu propriile lor raspunsuri. Cine intreaba cat timp intrebarea e
 * deja pusa primeste acelasi raspuns.
 */
export function refreshSession(): Promise<boolean> {
  if (inFlight !== null) return inFlight

  const asked = askForNewToken().finally(() => {
    inFlight = null
  })
  inFlight = asked

  return asked
}

/**
 * Un pas de ceas: reinnoieste daca e vremea SI daca omul e aici.
 *
 * Intoarce cererea cand a pornit una, altfel null - ca proba sa poata spune
 * diferenta fara sa se uite la `fetch`.
 */
export function renewIfWorking(now: number = Date.now()): Promise<boolean> | null {
  if (now - lastRenewAt < RENEW_AFTER_MS) return null
  if (now - lastInputAt > ACTIVE_WITHIN_MS) return null

  return refreshSession()
}

/**
 * Porneste ceasul. Chemat o data, din `main.tsx`. Intoarce cu ce se opreste -
 * de care are nevoie proba, nu aplicatia: fila traieste cat sesiunea.
 */
export function startSessionRenewal(): () => void {
  /* Incarcarea paginii e fapta unui om, si e singura clipa in care nu stim cat
     i-a mai ramas jetonului (cookie-ul e HttpOnly). */
  noteActivity()
  void refreshSession()

  const onInput = (): void => noteActivity()

  /* `capture`, ca sa se numere si ce opreste un ecran din drum; `passive`, ca
     sa nu tina in loc derularea. */
  for (const name of INPUT_EVENTS) {
    window.addEventListener(name, onInput, { capture: true, passive: true })
  }

  /* Intoarcerea in fila e si ea o fapta a omului - si e chiar clipa in care
     merita intrebat, fiindca poate a lipsit un sfert de ora. */
  const onVisible = (): void => {
    if (document.visibilityState !== 'visible') return
    noteActivity()
    void renewIfWorking()
  }
  document.addEventListener('visibilitychange', onVisible)

  const timer = window.setInterval(() => {
    void renewIfWorking()
  }, CHECK_EVERY_MS)

  return () => {
    window.clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisible)
    for (const name of INPUT_EVENTS) {
      window.removeEventListener(name, onInput, { capture: true })
    }
  }
}
